import PropTypes from 'prop-types';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { withFormik, Form } from 'formik';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Error, TableRowInput, Legal } from '@figure/frontlib-gui';
import { reduxPropTypes } from '@figure/frontlib-onboarding-redux';
import { FormErrorHandler, Typography } from 'shared/components';
import * as Types from 'shared/constants/ach-pay';
import AccountHeader from './AccountHeader';
import AccountList from './AccountList';
import LinkButton from './LinkButton';

const Container = styled.div`
  text-align: center;

  > :first-child {
    margin-bottom: 16px;
  }
`;

const Disbursement = ({
  values,
  errors,
  touched,
  handleLink,
  handleRetry,
  handleDelete,
  handleSubmit,
  setFieldValue,
  serverError,
  items,
  personalLoan,
}) => {
  const [t] = useTranslation();

  const headerText =
    items.length > 0
      ? 'Select the checking account where you’d like to receive your funds.'
      : 'Let’s link your checking account for disbursement.';
  const subText =
    items.length > 0
      ? ''
      : 'Please link a checking account in your name. We will use the account to set up disbursement.';

  return (
    <>
      <Typography.PageHeader>{headerText}</Typography.PageHeader>
      <h3>{subText}</h3>
      <FormErrorHandler errors={errors} touched={touched} serverError={serverError} />
      <Form onSubmit={handleSubmit}>
        {items.length > 0 && (
          <>
            <AccountList
              items={items}
              errorText={touched.achAccountId && errors.achAccountId}
              renderHeader={(item) => <AccountHeader item={item} handleDelete={handleDelete} />}
              renderRow={(a) =>
                a.accountType?.toUpperCase() === Types.CHECKING && (
                  <TableRowInput
                    key={`account-id-${a.plaidAccountId}`}
                    id={`account-id-${a.plaidAccountId}`}
                    name="account-id"
                    value={a.plaidAccountId}
                    checked={values.achAccountId === a.plaidAccountId}
                    required
                    data={
                      !!a.officialName && a.institution.toUpperCase() === 'CHASE'
                        ? [a.name, a.officialName, a.mask]
                        : [a.name, a.routingNumber || 'Unavailable', a.accountNumber]
                    }
                    onChange={() => {
                      setFieldValue('achAccountId', a.plaidAccountId);
                      setFieldValue('accountType', a.accountType);
                    }}
                  />
                )
              }
              handleRetry={handleRetry}
            />

            <Legal>
              By pressing “Save and continue,” you authorize {t('companySingleName')} to disburse
              your funds to the account provided.
            </Legal>
          </>
        )}

        <Error display="block">{serverError}</Error>

        <Container>
          <LinkButton handleClick={handleLink}>
            {items.length > 0 && (
              <Button onClick={handleSubmit} primary data-test-id="save-and-continue">
                Save and continue
              </Button>
            )}
          </LinkButton>
          {items.length === 0 && (
            <Link to={`/applications/${personalLoan.uuid}/bank/manual-disbursement`}>
              I’d prefer to manually enter my account information
            </Link>
          )}
        </Container>
      </Form>
    </>
  );
};

Disbursement.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  handleLink: PropTypes.func.isRequired,
  handleRetry: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  serverError: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(reduxPropTypes.plaidItem).isRequired,
  // TODO: Add personalLoan type
  // personalLoan: PropTypes.object.isRequired,
};

const FormikDisbursement = withFormik({
  enableReinitialize: true,
  mapPropsToValues: ({ personalLoan }) => ({
    plUuid: personalLoan.uuid || '',
    accountType: personalLoan?.disburseAccount?.accountType || '',
    achAccountId: personalLoan?.disburseAccount?.achAccountId || '',
  }),
  validationSchema: () =>
    yup.lazy(() =>
      yup.object().shape({
        plUuid: yup.string().required(),
        accountType: yup.string().required(),
        achAccountId: yup.string().required('Please select or add new account to continue'),
      })
    ),

  handleSubmit: (values, { props }) => props.handleSubmit(values),
})(Disbursement);

FormikDisbursement.propTypes = {
  // TODO: Replace with personalLoan types
  personalLoan: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    disburseAccount: PropTypes.shape({
      accountType: PropTypes.string,
      achAccountId: PropTypes.string,
    }),
  }).isRequired,
  handleSubmit: PropTypes.func.isRequired,
};

export default FormikDisbursement;
